import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Users, FolderOpen, CreditCard, ChartBar, ShieldCheck, MagnifyingGlass, X, Trash, Lightning, Calendar } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import logger from "@/lib/logger";

const TIERS = ["free", "base", "pro", "enterprise"];
const STATUSES = ["active", "expired", "cancelled"];

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{label}</div>
      <div className="font-display font-black text-3xl mt-2" data-testid={testid}>{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [txs, setTxs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [plans, setPlans] = useState(null);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [editingUser, setEditingUser] = useState(null);

  // Redirect non-admins
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin privileges required");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (e) { logger.warn(e); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (tierFilter) params.tier = tierFilter;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
    } catch (e) { toast.error("Failed to load users"); logger.warn(e); }
  }, [q, tierFilter]);

  const loadProjects = useCallback(async () => {
    try { const { data } = await api.get("/admin/projects", { params: { limit: 200 } }); setProjects(data.projects || []); } catch (e) { logger.warn(e); }
  }, []);

  const loadTxs = useCallback(async () => {
    try { const { data } = await api.get("/admin/transactions"); setTxs(data.transactions || []); } catch (e) { logger.warn(e); }
  }, []);

  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get("/admin/audit-log"); setAudit(data.audit || []); } catch (e) { logger.warn(e); }
  }, []);

  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get("/admin/plans"); setPlans(data); } catch (e) { logger.warn(e); }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    loadStats();
    loadUsers();
    loadProjects();
    loadTxs();
    loadAudit();
    loadPlans();
  }, [user, loadStats, loadUsers, loadProjects, loadTxs, loadAudit, loadPlans]);

  if (!user?.is_admin) return null;

  const saveUser = async (id, changes) => {
    try {
      await api.patch(`/admin/users/${id}`, changes);
      toast.success("User updated");
      await loadUsers();
      await loadAudit();
      await loadStats();
      setEditingUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Permanently delete ${email}? This also removes all their projects.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      await loadUsers();
      await loadAudit();
      await loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const deleteProject = async (id, name) => {
    if (!window.confirm(`Delete project "${name}"?`)) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      await loadProjects();
      await loadAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-950">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-zinc-950 text-white border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2 hover:text-[#00C7BE]" data-testid="back-to-dashboard">
              <ArrowLeft size={12} weight="bold" /> Dashboard
            </Link>
            <BrandMark size={32} invert />
            <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-[#00C7BE]">
              <ShieldCheck size={12} weight="fill" /> Super-admin
            </span>
          </div>
          <button onClick={logout} className="text-sm font-medium hover:text-white/70" data-testid="admin-logout">Sign out</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-8" data-testid="admin-page">
        <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mb-2">Operations Console</h1>
        <p className="text-zinc-600 text-sm mb-6">Logged in as <span className="font-mono">{user.email}</span></p>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats-grid">
          <Stat label="Total users" value={stats?.users_total ?? "—"} testid="stat-users-total" />
          <Stat label="Active subs (MRR)" value={`$${(stats?.mrr_cad ?? 0).toLocaleString()}`} testid="stat-mrr" />
          <Stat label="Projects" value={stats?.projects_total ?? "—"} testid="stat-projects-total" />
          <Stat label="Paid transactions" value={stats?.paid_transactions ?? "—"} testid="stat-paid-transactions" />
          <Stat label="Pro tier users" value={stats?.tier_counts?.pro ?? "—"} testid="stat-pro-users" />
        </div>

        {/* Tier breakdown */}
        {stats && (
          <div className="mt-3 bg-white border border-zinc-200 p-5" data-testid="tier-breakdown">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Tier breakdown</div>
            <div className="flex flex-wrap gap-3">
              {TIERS.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 h-7 px-3 bg-zinc-100 border border-zinc-200 text-xs font-mono" data-testid={`tier-${t}-count`}>
                  <span className="font-bold uppercase">{t}</span>
                  <span className="text-zinc-500">{stats.tier_counts[t] ?? 0}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 border-b border-zinc-300 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {[
            { key: "users", icon: Users, label: "Users" },
            { key: "projects", icon: FolderOpen, label: "Projects" },
            { key: "transactions", icon: CreditCard, label: "Transactions" },
            { key: "plans", icon: ChartBar, label: "Plans catalog" },
            { key: "audit", icon: ShieldCheck, label: "Audit log" },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={"h-11 px-4 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px " + (active ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950")}
                data-testid={`tab-${t.key}`}
              >
                <Icon size={14} weight={active ? "bold" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* USERS TAB */}
        {tab === "users" && (
          <div className="mt-6" data-testid="users-panel">
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <div className="relative">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-3.5 text-zinc-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadUsers()}
                  placeholder="Search email or name"
                  className="h-11 pl-9 pr-4 border border-zinc-300 bg-white text-sm w-72"
                  data-testid="users-search"
                />
              </div>
              <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="h-11 px-3 border border-zinc-300 bg-white text-sm" data-testid="users-tier-filter">
                <option value="">All tiers</option>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={loadUsers} className="h-11 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800" data-testid="users-apply-filters">Apply</button>
              <div className="text-sm text-zinc-500 ml-auto">{users.length} result{users.length === 1 ? "" : "s"}</div>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-zinc-50 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Industry</th>
                    <th className="text-right px-4 py-3">Projects</th>
                    <th className="text-left px-4 py-3">Joined</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">
                        {u.email}
                        {u.is_admin && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#00C7BE] text-white text-[9px] tracking-widest uppercase font-bold">admin</span>}
                      </td>
                      <td className="px-4 py-3">{u.full_name}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center h-6 px-2 bg-zinc-100 text-zinc-800 text-[10px] uppercase tracking-wider font-bold">{u.subscription_tier}</span></td>
                      <td className="px-4 py-3 text-xs">{u.subscription_status}</td>
                      <td className="px-4 py-3 text-xs">{u.industry}</td>
                      <td className="px-4 py-3 text-right font-mono">{u.project_count}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setEditingUser(u)} className="text-xs font-bold underline hover:text-[#0020F5] mr-3" data-testid={`edit-user-${u.id}`}>Edit</button>
                        {!u.is_admin && (
                          <button onClick={() => deleteUser(u.id, u.email)} className="text-xs text-[#FF3B30] hover:underline" data-testid={`delete-user-${u.id}`}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PROJECTS TAB */}
        {tab === "projects" && (
          <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto" data-testid="projects-panel">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Project</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Tier</th>
                  <th className="text-left px-4 py-3">Industry</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {projects.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-50" data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3 font-semibold">{p.name}</td>
                    <td className="px-4 py-3 text-xs font-mono">{p.owner?.email || "—"}</td>
                    <td className="px-4 py-3 text-xs uppercase">{p.owner?.subscription_tier || "—"}</td>
                    <td className="px-4 py-3 text-xs">{p.industry}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteProject(p.id, p.name)} className="text-xs text-[#FF3B30] hover:underline" data-testid={`delete-project-${p.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {tab === "transactions" && (
          <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto" data-testid="txs-panel">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {txs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500 text-sm">No transactions yet.</td></tr>
                )}
                {txs.map((t) => (
                  <tr key={t.session_id || t.id} className="hover:bg-zinc-50" data-testid={`tx-row-${t.session_id || t.id}`}>
                    <td className="px-4 py-3 text-xs text-zinc-500">{(t.created_at || "").slice(0, 16)}</td>
                    <td className="px-4 py-3 text-xs font-mono">{t.user?.email || t.user_id}</td>
                    <td className="px-4 py-3 text-xs">{t.plan_id || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">${t.amount?.toFixed(2)} {(t.currency || "").toUpperCase()}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className={"inline-flex items-center h-6 px-2 text-[10px] uppercase tracking-wider font-bold " + (t.payment_status?.includes("paid") ? "bg-[#00C7BE] text-white" : "bg-zinc-100 text-zinc-700")}>{t.payment_status || "open"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PLANS TAB */}
        {tab === "plans" && plans && (
          <div className="mt-6" data-testid="plans-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(plans.plans).map(([id, p]) => (
                <div key={id} className="bg-white border border-zinc-200 p-5" data-testid={`plan-${id}`}>
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">{p.tier} · {p.cycle}</div>
                  <div className="font-display font-bold text-lg mt-2">{p.name}</div>
                  <div className="font-display font-black text-3xl mt-3">${p.amount} <span className="text-zinc-400 text-base uppercase">{p.currency}</span></div>
                  <div className="text-xs text-zinc-500 mt-3">Duration · {p.duration_days} days</div>
                  <div className="text-xs text-zinc-400 mt-1 font-mono">{id}</div>
                </div>
              ))}
            </div>

            <h3 className="font-display font-bold text-xl mt-10 mb-3">Tier limits</h3>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-right px-4 py-3">Max projects</th>
                    <th className="text-left px-4 py-3">Weekly brief</th>
                    <th className="text-left px-4 py-3">Email brief</th>
                    <th className="text-left px-4 py-3">Priority AI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {Object.entries(plans.tier_limits).map(([tier, l]) => (
                    <tr key={tier}>
                      <td className="px-4 py-3 font-bold uppercase text-xs">{tier}</td>
                      <td className="px-4 py-3 text-right font-mono">{l.max_projects === 9999 ? "∞" : l.max_projects}</td>
                      <td className="px-4 py-3 text-xs">{l.weekly_brief ? "✅" : "—"}</td>
                      <td className="px-4 py-3 text-xs">{l.email_brief ? "✅" : "—"}</td>
                      <td className="px-4 py-3 text-xs">{l.priority_ai ? "✅" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AUDIT TAB */}
        {tab === "audit" && (
          <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto" data-testid="audit-panel">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Actor</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Target</th>
                  <th className="text-left px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {audit.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500 text-sm">No admin actions yet.</td></tr>}
                {audit.map((a) => (
                  <tr key={a.id} className="hover:bg-zinc-50" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-3 text-xs text-zinc-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-xs font-mono">{a.actor_email}</td>
                    <td className="px-4 py-3 text-xs"><span className="inline-flex items-center h-6 px-2 bg-zinc-100 text-zinc-800 text-[10px] uppercase tracking-wider font-bold">{a.action}</span></td>
                    <td className="px-4 py-3 text-xs">{a.target_label || a.target_id || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{JSON.stringify(a.meta || {})}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Edit user drawer */}
      {editingUser && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm" onClick={() => setEditingUser(null)} data-testid="user-edit-drawer">
          <aside className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-white border-l border-zinc-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-7 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Edit user</div>
                <div className="font-mono text-sm mt-1">{editingUser.email}</div>
              </div>
              <button onClick={() => setEditingUser(null)} className="w-9 h-9 grid place-items-center hover:bg-zinc-100" data-testid="close-edit-drawer"><X size={16} /></button>
            </div>
            <div className="p-7 space-y-5 flex-1 overflow-y-auto">
              <label className="block">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Subscription tier</span>
                <select defaultValue={editingUser.subscription_tier} className="w-full mt-2 h-11 px-3 border border-zinc-300 bg-white text-sm" data-testid="edit-tier-select" onChange={(e) => { editingUser._tier = e.target.value; }}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Status</span>
                <select defaultValue={editingUser.subscription_status} className="w-full mt-2 h-11 px-3 border border-zinc-300 bg-white text-sm" data-testid="edit-status-select" onChange={(e) => { editingUser._status = e.target.value; }}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Extend subscription (days)</span>
                <input type="number" placeholder="e.g. 30" className="w-full mt-2 h-11 px-3 border border-zinc-300 bg-white text-sm" data-testid="edit-extend-days" onChange={(e) => { editingUser._extend = parseInt(e.target.value || "0", 10); }} />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Full name</span>
                <input defaultValue={editingUser.full_name} className="w-full mt-2 h-11 px-3 border border-zinc-300 bg-white text-sm" data-testid="edit-name-input" onChange={(e) => { editingUser._name = e.target.value; }} />
              </label>
              <div className="bg-zinc-100 border border-zinc-200 p-4 text-xs text-zinc-700 flex items-start gap-2">
                <Lightning size={14} weight="bold" className="mt-0.5 text-[#FF9500] shrink-0" />
                <div>Quick actions: bump <strong>extend days</strong> by 30 to grant a free month; change tier to <strong>enterprise</strong> for a manual upgrade.</div>
              </div>
            </div>
            <div className="p-7 border-t border-zinc-200 flex justify-end gap-3">
              <button onClick={() => setEditingUser(null)} className="h-11 px-5 border border-zinc-300 text-sm font-bold hover:bg-zinc-100">Cancel</button>
              <button onClick={() => saveUser(editingUser.id, {
                subscription_tier: editingUser._tier ?? editingUser.subscription_tier,
                subscription_status: editingUser._status ?? editingUser.subscription_status,
                extend_days: editingUser._extend || undefined,
                full_name: editingUser._name && editingUser._name !== editingUser.full_name ? editingUser._name : undefined,
              })} className="h-11 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800" data-testid="save-user-edit">Save changes</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
